// Invoice print/PDF utilities with header and duplicate watermark, inspired by doctor pdfGenerator.js

// Normalize any numeric-ish input so stray quotes/commas do not break rendering
const normalizeNumber = (v) => {
  const cleaned = String(v ?? '0').replace(/[^0-9.-]/g, '');
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : 0;
};

const formatNumber = (v, opts = {}) => normalizeNumber(v).toLocaleString('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  ...opts,
});

const formatCurrency = (v) => `₹${formatNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// jsPDF built-in Helvetica lacks the rupee glyph; use ASCII-safe fallback for PDF only
const formatCurrencyPdf = (v) => `INR ${formatNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getHospitalName = (preview) => {
  // Prefer backend-provided hospital name if present
  const name = preview?.invoice?.hospitalDetails?.name
    || preview?.hospitalDetails?.name
    || 'HealthCare Plus Hospital';
  return name;
};

const escapeHtml = (s = '') => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

export const generateInvoicePrintHTML = (preview, { duplicate = false } = {}) => {
  const hospitalName = getHospitalName(preview);
  const inv = preview?.invoice || {};
  const pat = preview?.patient || {};
  const doc = preview?.doctor || {};
  const apt = preview?.appointment || {};
  const items = preview?.items || {};

  const invoiceDateText = inv.date ? new Date(inv.date).toLocaleString('en-IN', { hour12: true }) : '-';
  const apptDateText = apt?.dateTime ? new Date(apt.dateTime).toLocaleString('en-IN', { hour12: true }) : '-';

  const taxRate = items?.doctorCharges?.taxRate || items?.tests?.[0]?.taxRate || items?.medicines?.[0]?.taxRate || items?.imagingTests?.[0]?.taxRate || 18;

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Invoice ${escapeHtml(inv.invoiceNumber || '')}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 0;
          background: white;
          color: black;
          line-height: 1.5;
          font-size: 14px;
          position: relative;
          z-index: 0;
        }
        .content-wrapper {
          margin: 12px 24px;
          padding-top: 0;
          position: relative;
          z-index: 1;
        }
        /* Hospital Header - blue title same as prescription */
        .hospital-header {
          background: white; padding: 12px 24px 10px 24px; border-bottom: 2px solid #0066cc; margin-bottom: 8px;
          position: relative; z-index: 1;
        }
        .hospital-header h1 { color: #0066cc; margin: 0; font-size: 16px; font-weight: bold; text-align: center; }
        /* Context bar */
        .context-bar { background: white; padding: 8px 24px; font-size: 11px; color: #282828; border-bottom: 1px solid #b4b4b4; margin-bottom: 14px; position: relative; z-index: 1; }
        .context-bar .row { display: flex; justify-content: space-between; margin-bottom: 4px; line-height: 1.3; }
        .context-bar .row:last-child { margin-bottom: 0; }
        /* Sections/cards - remove cards, just use plain sections */
        .section { margin: 14px 0; }
        .section h2 { color: #000; font-size: 13px; font-weight: bold; margin-bottom: 6px; page-break-after: avoid; break-after: avoid; }
        /* Tables */
        table { width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 4px; }
        tr { page-break-inside: avoid; break-inside: avoid; }
        th, td { border-top: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
        tbody tr:nth-child(even) { background: #f9fafb; }
        tbody tr:nth-child(odd) { background: #ffffff; }
        th { background: #f9fafb; font-weight: 600; font-size: 10px; color: #374151; }
        /* Grid */
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 11px; }
        .right { text-align: right; }
        .bold { font-weight: 600; }
        .num {
          font-family: "Segoe UI", "Noto Sans", "Arial Unicode MS", Arial, sans-serif;
          font-variant-numeric: tabular-nums;
          font-feature-settings: "tnum";
          letter-spacing: 0;
          white-space: nowrap;
        }
        /* Watermark */
        .watermark { position: fixed; inset: 0; z-index: 0; pointer-events: none; user-select: none; overflow: hidden; display: flex; align-items: center; justify-content: center; }
        .watermark span { transform: rotate(-45deg); font-size: 100px; color: rgba(210,210,210,0.35); font-weight: 700; letter-spacing: 10px; text-transform: uppercase; white-space: nowrap; }
        /* Totals box */
        .totals-box {
          border: 1px solid #e5e7eb;
          padding: 8px 10px;
          margin-top: 10px;
          width: 220px;
          float: right;
          font-size: 11px;
        }
        .totals-box .row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;
        }
        .totals-box .row.total {
          margin-top: 6px;
          padding-top: 6px;
          border-top: 1px solid #000;
          font-weight: bold;
          font-size: 13px;
        }
        @media print {
          @page { size: auto; margin: 15mm 12mm 15mm 12mm; }
          body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .content-wrapper { margin: 0; padding: 0; }
          .hospital-header { padding: 8px 12mm 6px 12mm; }
          .context-bar { padding: 6px 12mm; }
          .section { margin-left: 12mm; margin-right: 12mm; }
          table th, tbody tr:nth-child(even) { background: #f9fafb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          tbody tr:nth-child(odd) { background: #ffffff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none; }
          .watermark { -webkit-print-color-adjust: exact; print-color-adjust: exact; width: 100%; height: 100%; }
          .watermark span { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      </style>
    </head>
    <body>
      ${duplicate ? '<div class="watermark"><span>DUPLICATE COPY</span></div>' : ''}
      <div class="hospital-header"><h1>${escapeHtml(hospitalName)}</h1></div>
      <div class="context-bar">
        <div class="row"><span>Invoice #: ${escapeHtml(inv.invoiceNumber || '')}</span><span>Invoice Date: ${escapeHtml(invoiceDateText)}</span></div>
        <div class="row"><span>Patient: ${escapeHtml(pat.name || '-')}</span><span>Doctor: ${escapeHtml(doc.name || '-')}</span></div>
        <div class="row"><span>IP Registration Number: ${escapeHtml(pat.externalId || '-')}</span><span></span></div>
      </div>

      <div class="content-wrapper">
        <div class="section">
          <h2>Patient</h2>
          <div class="grid">
            <div>IP Registration Number: <span class="bold">${escapeHtml(pat.externalId || '-')}</span></div>
            <div>Name: <span class="bold">${escapeHtml(pat.name || '-')}</span></div>
            <div>Phone: <span class="bold">${escapeHtml(pat.phone || '-')}</span></div>
            <div>Email: <span class="bold">${escapeHtml(pat.email || '-')}</span></div>
            <div style="grid-column: 1 / -1;">Address: <span class="bold">${escapeHtml(pat.address || '-')}</span></div>
          </div>
        </div>

        <div class="section">
          <h2>Consulting Doctor</h2>
          <div class="grid">
            <div>Name: <span class="bold">${escapeHtml(doc.name || '-')}</span></div>
            <div>Reg. No.: <span class="bold">${escapeHtml(doc.registrationNumber || '-')}</span></div>
            <div>Department: <span class="bold">${escapeHtml(doc.department || '-')}</span></div>
            <div>Specialization: <span class="bold">${escapeHtml(doc.specialization || '-')}</span></div>
            <div style="grid-column: 1 / -1;">Appt. Date & Time: <span class="bold">${escapeHtml(apptDateText)}</span></div>
          </div>
        </div>

        <div class="section">
          <h2>Invoice Details</h2>
          ${items?.doctorCharges ? `<div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:6px;"><div>Consultation - ${escapeHtml(items.doctorCharges.consultationType || '')}</div><div class="bold num">${formatCurrency(items.doctorCharges.amount || 0)}</div></div>` : ''}
          
          ${(items?.medicines && items.medicines.length > 0) ? `
          <div style="margin-bottom:10px;">
            <div style="font-size:11px;font-weight:bold;margin-bottom:4px;">Medicines</div>
            <table>
              <thead><tr><th>Medicine</th><th class="right num">Qty</th><th class="right num">Unit Price</th><th class="right num">Total</th></tr></thead>
              <tbody>
                ${items.medicines.map(m => `
                  <tr>
                    <td>${escapeHtml(m.name || '')}</td>
                    <td class="right num">${formatNumber(m.quantity || 0)}</td>
                    <td class="right num">${formatCurrency(m.unitPrice || 0)}</td>
                    <td class="right num">${formatCurrency((m.unitPrice || 0) * (m.quantity || 1))}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          ` : ''}

          ${(items?.tests && items.tests.length > 0) ? `
          <div style="margin-bottom:10px;">
            <div style="font-size:11px;font-weight:bold;margin-bottom:4px;">Tests</div>
            <table>
              <thead><tr><th>Test</th><th class="right num">Qty</th><th class="right num">Unit Price</th><th class="right num">Total</th></tr></thead>
              <tbody>
                ${items.tests.map(t => `
                  <tr>
                    <td>${escapeHtml(t.name || '')}</td>
                    <td class="right num">${formatNumber(t.quantity || 0)}</td>
                    <td class="right num">${formatCurrency(t.unitPrice || 0)}</td>
                    <td class="right num">${formatCurrency((t.unitPrice || 0) * (t.quantity || 1))}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          ` : ''}

          ${(items?.imagingTests && items.imagingTests.length > 0) ? `
          <div style="margin-bottom:10px;">
            <div style="font-size:11px;font-weight:bold;margin-bottom:4px;">Imaging Tests</div>
            <table>
              <thead><tr><th>Test Name</th><th>Modality</th><th class="right num">Qty</th><th class="right num">Unit Price</th><th class="right num">Total</th></tr></thead>
              <tbody>
                ${items.imagingTests.map(img => `
                  <tr>
                    <td>${escapeHtml(img.name || '')}</td>
                    <td>${escapeHtml(img.modality || '-')}</td>
                    <td class="right num">${formatNumber(img.quantity || 0)}</td>
                    <td class="right num">${formatCurrency(img.unitPrice || 0)}</td>
                    <td class="right num">${formatCurrency((img.unitPrice || 0) * (img.quantity || 1))}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          ` : ''}

          <div class="totals-box">
            <div class="row"><span>Total Amount</span><span class="num">${formatCurrency(inv?.totals?.subtotal || 0)}</span></div>
            <div class="row"><span>Tax (GST) (${taxRate}%)</span><span class="num">${formatCurrency(inv?.totals?.tax || 0)}</span></div>
            <div class="row"><span>Paid Amount</span><span class="num">${formatCurrency(inv?.totals?.amountPaid || 0)}</span></div>
            <div class="row total"><span>Grand Total</span><span class="num">${formatCurrency(inv?.totals?.grandTotal || 0)}</span></div>
          </div>
          <div style="clear:both;"></div>
        </div>

        <div class="section">
          <h2>Payment</h2>
          <div class="grid">
            <div>Mode: <span class="bold">${escapeHtml(preview?.payment?.mode || '-')}</span></div>
            <div>Due: <span class="bold num">${formatCurrency(preview?.payment?.due || 0)}</span></div>
            <div style="grid-column: 1 / -1;">Collected By: <span class="bold">${escapeHtml(preview?.payment?.collectedBy?.name || '-')}</span></div>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

// Fragment HTML specifically for html2canvas capture (static header + margins)
export const generateInvoiceCaptureHTML = (preview, { duplicate = false } = {}) => {
  const hospitalName = getHospitalName(preview);
  const inv = preview?.invoice || {};
  const pat = preview?.patient || {};
  const doc = preview?.doctor || {};
  const apt = preview?.appointment || {};
  const items = preview?.items || {};

  const invoiceDateText = inv.date ? new Date(inv.date).toLocaleString('en-IN', { hour12: true }) : '-';
  const apptDateText = apt?.dateTime ? new Date(apt.dateTime).toLocaleString('en-IN', { hour12: true }) : '-';

  return `
    <style>
      .inv-root { font-family: Arial, sans-serif; width: 794px; box-sizing: border-box; padding: 24px 60px; background: #ffffff; color: #000; line-height: 1.5; font-size: 14px; position: relative; z-index: 0; }
      .inv-header { background: #fff; padding: 8px 0 6px 0; border-bottom: 2px solid #0066cc; margin-bottom: 8px; position: relative; z-index: 1; }
      .inv-header h1 { color: #0066cc; margin: 0; font-size: 18px; font-weight: bold; text-align: center; }
      .inv-context { background: #fff; padding: 8px 0; border-bottom: 1px solid #b4b4b4; margin-bottom: 14px; font-size: 12px; color: #282828; position: relative; z-index: 1; }
      .inv-row { display: flex; justify-content: space-between; margin-bottom: 4px; line-height: 1.3; }
      .inv-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 14px; background: transparent; margin: 14px 0; position: relative; z-index: 1; }
      .inv-card h2 { color: #000; font-size: 14px; font-weight: bold; margin: 0 0 8px 0; }
      .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { border-top: 1px solid #e5e7eb; padding: 8px; text-align: left; }
      th { background: #f9fafb; font-weight: 600; }
      .right { text-align: right; }
      .bold { font-weight: 600; }
      .num {
        font-family: "Segoe UI", "Noto Sans", "Arial Unicode MS", Arial, sans-serif;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
        letter-spacing: 0;
        white-space: nowrap;
      }
      /* Watermark as absolute inside root for capture */
      .wm { position: absolute; inset: 0; overflow: hidden; pointer-events: none; user-select: none; z-index: 0; display: flex; align-items: center; justify-content: center; }
      .wm span { transform: rotate(-45deg); font-size: 85px; color: rgba(210,210,210,0.35); font-weight: 700; letter-spacing: 8px; text-transform: uppercase; white-space: nowrap; }
    </style>
    <div class="inv-root">
      ${duplicate ? '<div class="wm"><span>DUPLICATE COPY</span></div>' : ''}
      <div class="inv-header"><h1>${escapeHtml(hospitalName)}</h1></div>
      <div class="inv-context">
        <div class="inv-row"><span>Invoice #: ${escapeHtml(inv.invoiceNumber || '')}</span><span>Invoice Date: ${escapeHtml(invoiceDateText)}</span></div>
        <div class="inv-row"><span>Patient: ${escapeHtml(pat.name || '-')}</span><span>Doctor: ${escapeHtml(doc.name || '-')}</span></div>
        <div class="inv-row"><span>IP Registration Number: ${escapeHtml(pat.externalId || '-')}</span><span></span></div>
      </div>

      <div class="inv-card">
        <h2>Patient</h2>
        <div class="grid-2">
          <div>IP Registration Number: <span class="bold">${escapeHtml(pat.externalId || '-')}</span></div>
          <div>Name: <span class="bold">${escapeHtml(pat.name || '-')}</span></div>
          <div>Phone: <span class="bold">${escapeHtml(pat.phone || '-')}</span></div>
          <div>Email: <span class="bold">${escapeHtml(pat.email || '-')}</span></div>
          <div style="grid-column: 1 / -1;">Address: <span class="bold">${escapeHtml(pat.address || '-')}</span></div>
        </div>
      </div>

      <div class="inv-card">
        <h2>Consulting Doctor</h2>
        <div class="grid-2">
          <div>Name: <span class="bold">${escapeHtml(doc.name || '-')}</span></div>
          <div>Reg. No.: <span class="bold">${escapeHtml(doc.registrationNumber || '-')}</span></div>
          <div>Department: <span class="bold">${escapeHtml(doc.department || '-')}</span></div>
          <div>Specialization: <span class="bold">${escapeHtml(doc.specialization || '-')}</span></div>
          <div style="grid-column: 1 / -1;">Appt. Date & Time: <span class="bold">${escapeHtml(apptDateText)}</span></div>
        </div>
      </div>

      <div class="inv-card">
        <h2>Invoice Details</h2>
        ${items?.doctorCharges ? `<div class="inv-row" style="font-size:12px;margin-bottom:6px;"><div>Consultation - ${escapeHtml(items.doctorCharges.consultationType || '')}</div><div class="bold num">${formatCurrency(items.doctorCharges.total || 0)}</div></div>` : ''}
        <div class="grid-2">
          <div>
            <div style="font-size:11px;color:#4b5563;margin-bottom:4px;">Medicines</div>
            <table>
              <thead><tr><th>Medicine</th><th class="right num">Qty</th><th class="right num">Unit</th><th class="right num">Total</th></tr></thead>
              <tbody>
                ${(items?.medicines || []).map(m => `
                  <tr>
                    <td>${escapeHtml(m.name || '')}</td>
                    <td class="right num">${formatNumber(m.quantity || 0)}</td>
                    <td class="right num">${formatCurrency(m.unitPrice || 0)}</td>
                    <td class="right num">${formatCurrency(m.total || 0)}</td>
                  </tr>
                `).join('')}
                ${(!items?.medicines || items.medicines.length === 0) ? '<tr><td colspan="4" style="color:#6b7280;">No medicines</td></tr>' : ''}
              </tbody>
            </table>
          </div>
          <div>
            <div style="font-size:11px;color:#4b5563;margin-bottom:4px;">Tests</div>
            <table>
              <thead><tr><th>Test</th><th class="right num">Qty</th><th class="right num">Unit</th><th class="right num">Total</th></tr></thead>
              <tbody>
                ${(items?.tests || []).map(t => `
                  <tr>
                    <td>${escapeHtml(t.name || '')}</td>
                    <td class="right num">${formatNumber(t.quantity || 0)}</td>
                    <td class="right num">${formatCurrency(t.unitPrice || 0)}</td>
                    <td class="right num">${formatCurrency(t.total || 0)}</td>
                  </tr>
                `).join('')}
                ${(!items?.tests || items.tests.length === 0) ? '<tr><td colspan="4" style="color:#6b7280;">No tests</td></tr>' : ''}
              </tbody>
            </table>
          </div>
        </div>
        ${(items?.imagingTests && items.imagingTests.length > 0) ? `
        <div style="margin-top:12px;">
          <div style="font-size:11px;color:#4b5563;margin-bottom:4px;">Imaging Tests</div>
          <table>
              <thead><tr><th>Test Name</th><th>Modality</th><th>Body Part</th><th class="right num">Qty</th><th class="right num">Unit</th><th class="right num">Total</th></tr></thead>
            <tbody>
              ${items.imagingTests.map(img => `
                <tr>
                  <td>${escapeHtml(img.name || '')}</td>
                  <td>${escapeHtml(img.modality || '-')}</td>
                  <td>${escapeHtml(img.bodyPart || '-')}</td>
                    <td class="right num">${formatNumber(img.quantity || 0)}</td>
                    <td class="right num">${formatCurrency(img.unitPrice || 0)}</td>
                    <td class="right num">${formatCurrency(img.total || 0)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}
        <div style="margin-top:8px;font-size:12px;">
          <div style="display:flex;justify-content:space-between;"> <span>Subtotal</span> <span class="num">${formatCurrency(inv?.totals?.subtotal || 0)}</span> </div>
          <div style="display:flex;justify-content:space-between;"> <span>Tax</span> <span class="num">${formatCurrency(inv?.totals?.tax || 0)}</span> </div>
          <div style="display:flex;justify-content:space-between;"> <span>Discount</span> <span class="num">${formatCurrency(inv?.totals?.discount || 0)}</span> </div>
          <div style="display:flex;justify-content:space-between;"> <span>Paid Amount</span> <span class="num">${formatCurrency(inv?.totals?.amountPaid || 0)}</span> </div>
          <div style="margin-top:6px;display:flex;justify-content:space-between;font-weight:600;"> <span>Grand Total</span> <span class="num">${formatCurrency(inv?.totals?.grandTotal || 0)}</span> </div>
        </div>
      </div>

      <div class="inv-card">
        <h2>Payment</h2>
        <div class="grid-2">
          <div>Mode: <span class="bold">${escapeHtml(preview?.payment?.mode || '-')}</span></div>
          <div>Due: <span class="bold num">${formatCurrency(preview?.payment?.due || 0)}</span></div>
          <div style="grid-column: 1 / -1;">Collected By: <span class="bold">${escapeHtml(preview?.payment?.collectedBy?.name || '-')}</span></div>
        </div>
      </div>
    </div>
  `;
};

export const printInvoiceHTML = (html) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  // Wait for resources to load
  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 500);
};

export const printInvoice = (preview, { duplicate = false } = {}) => {
  const html = generateInvoicePrintHTML(preview, { duplicate });
  printInvoiceHTML(html);
};

export const downloadInvoicePDF = async (preview, { duplicate = false } = {}) => {
  try {
    const { jsPDF } = await import('jspdf');
    
    const hospitalName = getHospitalName(preview);
    const inv = preview?.invoice || {};
    const pat = preview?.patient || {};
    const doc = preview?.doctor || {};
    const apt = preview?.appointment || {};
    const items = preview?.items || {};

    const invoiceDateText = inv.date ? new Date(inv.date).toLocaleString('en-IN', { hour12: true }) : '-';
    const apptDateText = apt?.dateTime ? new Date(apt.dateTime).toLocaleString('en-IN', { hour12: true }) : '-';

    const taxRate = items?.doctorCharges?.taxRate || items?.tests?.[0]?.taxRate || items?.medicines?.[0]?.taxRate || items?.imagingTests?.[0]?.taxRate || 18;

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (2 * margin);
    let yPos = margin;

    // Helper function to add header to each page
    const addHeader = () => {
      yPos = margin;
      
      // Hospital Name - Blue Title
      pdf.setFontSize(16);
      pdf.setTextColor(0, 102, 204);
      pdf.setFont('helvetica', 'bold');
      pdf.text(hospitalName, pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;
      
      // Blue line
      pdf.setDrawColor(0, 102, 204);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 6;
      
      // Context info
      pdf.setFontSize(10);
      pdf.setTextColor(40, 40, 40);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Invoice #: ${escapeHtml(inv.invoiceNumber || '')}`, margin, yPos);
      pdf.text(`Invoice Date: ${escapeHtml(invoiceDateText)}`, pageWidth - margin, yPos, { align: 'right' });
      yPos += 4;
      pdf.text(`IP Registration Number: ${escapeHtml(pat.externalId || '-')}`, margin, yPos);
      pdf.text(`Patient: ${escapeHtml(pat.name || '-')}`, margin, yPos + 4);
      pdf.text(`Doctor: ${escapeHtml(doc.name || '-')}`, pageWidth - margin, yPos + 4, { align: 'right' });
      yPos += 10;
      
      // Gray line
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;
      
      // Watermark
      if (duplicate) {
        pdf.saveGraphicsState();
        pdf.setTextColor(240, 240, 240); // Very light gray
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(75);
        const angle = 45;
        try {
          pdf.text('DUPLICATE COPY', 40, pageHeight - 60, { angle: angle, align: 'left' });
        } catch (e) {
          pdf.text('DUPLICATE COPY', 40, pageHeight - 60);
        }
        pdf.restoreGraphicsState();
      }
    };

    // Check if we need a new page
    const checkPageBreak = (requiredSpace) => {
      if (yPos + requiredSpace > pageHeight - margin) {
        pdf.addPage();
        addHeader();
        return true;
      }
      return false;
    };

    // Initial header
    addHeader();

    // Patient Section
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Patient', margin, yPos);
    yPos += 6;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    // IP Registration No (Line 1)
    pdf.setFont('helvetica', 'normal');
    pdf.text(`IP Registration Number: ${escapeHtml(pat.externalId || '-')}`, margin, yPos);
    yPos += 5;
    
    // Name and Phone (Line 2)
    pdf.text(`Name: ${escapeHtml(pat.name || '-')}`, margin, yPos);
    pdf.text(`Phone: ${escapeHtml(pat.phone || '-')}`, margin + contentWidth / 2, yPos);
    yPos += 5;
    
    // Line 2: Email
    pdf.text(`Email: ${escapeHtml(pat.email || '-')}`, margin, yPos);
    yPos += 5;
    
    // Line 3: Address
    pdf.text(`Address: ${escapeHtml(pat.address || '-')}`, margin, yPos);
    yPos += 10;

    checkPageBreak(30);

    // Doctor Section
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Consulting Doctor', margin, yPos);
    yPos += 6;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Name: ${escapeHtml(doc.name || '-')}`, margin, yPos);
    pdf.text(`Reg. No.: ${escapeHtml(doc.registrationNumber || '-')}`, margin + contentWidth / 2, yPos);
    yPos += 5;
    pdf.text(`Department: ${escapeHtml(doc.department || '-')}`, margin, yPos);
    pdf.text(`Specialization: ${escapeHtml(doc.specialization || '-')}`, margin + contentWidth / 2, yPos);
    yPos += 5;
    pdf.text(`Appt. Date & Time: ${escapeHtml(apptDateText)}`, margin, yPos);
    yPos += 10;

    checkPageBreak(20);

    // Invoice Details
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Invoice Details', margin, yPos);
    yPos += 6;

    // Doctor Charges
    if (items?.doctorCharges) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Consultation - ${escapeHtml(items.doctorCharges.consultationType || '')}`, margin, yPos);
      pdf.text(formatCurrencyPdf(items.doctorCharges.amount || 0), pageWidth - margin, yPos, { align: 'right' });
      yPos += 6;
    }

    // Medicines Table
    if (items?.medicines && items.medicines.length > 0) {
      checkPageBreak(15);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Medicines', margin, yPos);
      yPos += 6;
      
      // Table Header
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, yPos - 4, contentWidth, 6, 'F');
      pdf.setDrawColor(229, 231, 235);
      pdf.rect(margin, yPos - 4, contentWidth, 6, 'S');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(55, 65, 81);
      pdf.text('Medicine', margin + 2, yPos);
      pdf.text('Qty', margin + 115, yPos, { align: 'center' });
      pdf.text('Unit Price', margin + 140, yPos, { align: 'right' });
      pdf.text('Total', pageWidth - margin - 2, yPos, { align: 'right' });
      yPos += 6;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(9);
      
      items.medicines.forEach((m, idx) => {
        checkPageBreak(7);
        
        // Alternating row background
        if (idx % 2 === 0) {
          pdf.setFillColor(255, 255, 255);
        } else {
          pdf.setFillColor(249, 250, 251);
        }
        pdf.rect(margin, yPos - 4, contentWidth, 6, 'F');
        
        // Border
        pdf.setDrawColor(229, 231, 235);
        pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
        
        pdf.text(escapeHtml(m.name || '').substring(0, 45), margin + 2, yPos);
        pdf.text(formatNumber(m.quantity || 0), margin + 115, yPos, { align: 'center' });
        pdf.text(formatCurrencyPdf(m.unitPrice || 0), margin + 140, yPos, { align: 'right' });
        pdf.text(formatCurrencyPdf((m.unitPrice || 0) * (m.quantity || 1)), pageWidth - margin - 2, yPos, { align: 'right' });
        yPos += 6;
      });
      yPos += 4;
    }

    // Tests Table
    if (items?.tests && items.tests.length > 0) {
      checkPageBreak(15);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Tests', margin, yPos);
      yPos += 6;
      
      // Table Header
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, yPos - 4, contentWidth, 6, 'F');
      pdf.setDrawColor(229, 231, 235);
      pdf.rect(margin, yPos - 4, contentWidth, 6, 'S');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(55, 65, 81);
      pdf.text('Test', margin + 2, yPos);
      pdf.text('Qty', margin + 115, yPos, { align: 'center' });
      pdf.text('Unit Price', margin + 140, yPos, { align: 'right' });
      pdf.text('Total', pageWidth - margin - 2, yPos, { align: 'right' });
      yPos += 6;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(9);
      
      items.tests.forEach((t, idx) => {
        checkPageBreak(7);
        
        // Alternating row background
        if (idx % 2 === 0) {
          pdf.setFillColor(255, 255, 255);
        } else {
          pdf.setFillColor(249, 250, 251);
        }
        pdf.rect(margin, yPos - 4, contentWidth, 6, 'F');
        
        // Border
        pdf.setDrawColor(229, 231, 235);
        pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
        
        pdf.text(escapeHtml(t.name || '').substring(0, 45), margin + 2, yPos);
        pdf.text(formatNumber(t.quantity || 0), margin + 115, yPos, { align: 'center' });
        pdf.text(formatCurrencyPdf(t.unitPrice || 0), margin + 140, yPos, { align: 'right' });
        pdf.text(formatCurrencyPdf((t.unitPrice || 0) * (t.quantity || 1)), pageWidth - margin - 2, yPos, { align: 'right' });
        yPos += 6;
      });
      yPos += 4;
    }

    // Imaging Tests Table
    if (items?.imagingTests && items.imagingTests.length > 0) {
      checkPageBreak(15);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Imaging Tests', margin, yPos);
      yPos += 6;
      
      // Table Header
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, yPos - 4, contentWidth, 6, 'F');
      pdf.setDrawColor(229, 231, 235);
      pdf.rect(margin, yPos - 4, contentWidth, 6, 'S');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(55, 65, 81);
      pdf.text('Test Name', margin + 2, yPos);
      pdf.text('Modality', margin + 80, yPos);
      pdf.text('Qty', margin + 115, yPos, { align: 'center' });
      pdf.text('Unit Price', margin + 140, yPos, { align: 'right' });
      pdf.text('Total', pageWidth - margin - 2, yPos, { align: 'right' });
      yPos += 6;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(9);
      
      items.imagingTests.forEach((img, idx) => {
        const nameLines = pdf.splitTextToSize(escapeHtml(img.name || ''), 75);
        const linesCount = nameLines.length;
        const lineHeight = 4;
        const rowHeight = Math.max(6, linesCount * lineHeight + 2);

        checkPageBreak(rowHeight);
        
        // Alternating row background
        if (idx % 2 === 0) {
          pdf.setFillColor(255, 255, 255);
        } else {
          pdf.setFillColor(249, 250, 251);
        }
        pdf.rect(margin, yPos - 4, contentWidth, rowHeight, 'F');
        
        // Border
        pdf.setDrawColor(229, 231, 235);
        pdf.line(margin, yPos - 4 + rowHeight, pageWidth - margin, yPos - 4 + rowHeight);
        
        pdf.text(nameLines, margin + 2, yPos);
        pdf.text(escapeHtml(img.modality || '-').substring(0, 12), margin + 80, yPos);
        pdf.text(formatNumber(img.quantity || 0), margin + 115, yPos, { align: 'center' });
        pdf.text(formatCurrencyPdf(img.unitPrice || 0), margin + 140, yPos, { align: 'right' });
        pdf.text(formatCurrencyPdf((img.unitPrice || 0) * (img.quantity || 1)), pageWidth - margin - 2, yPos, { align: 'right' });
        yPos += rowHeight;
      });
      yPos += 4;
    }

    // Totals
    checkPageBreak(35);
    yPos += 5;
    
    // Draw totals box
    const totalsBoxY = yPos;
    const hasDiscount = (inv?.totals?.discount || 0) > 0;
    const totalsBoxHeight = hasDiscount ? 42 : 36;
    const totalsBoxWidth = 80;
    
    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.5);
    pdf.rect(pageWidth - margin - totalsBoxWidth, totalsBoxY, totalsBoxWidth, totalsBoxHeight);
    
    yPos += 5;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Total Amount', pageWidth - margin - (totalsBoxWidth - 5), yPos);
    pdf.text(formatCurrencyPdf(inv?.totals?.subtotal || 0), pageWidth - margin - 2, yPos, { align: 'right' });
    yPos += 6;
    
    pdf.text(`Tax (GST) (${taxRate}%)`, pageWidth - margin - (totalsBoxWidth - 5), yPos);
    pdf.text(formatCurrencyPdf(inv?.totals?.tax || 0), pageWidth - margin - 2, yPos, { align: 'right' });
    yPos += 6;

    if (hasDiscount) {
      pdf.setTextColor(220, 53, 69); // Red
      pdf.text('Discount', pageWidth - margin - (totalsBoxWidth - 5), yPos);
      pdf.text(`-${formatCurrencyPdf(inv?.totals?.discount || 0)}`, pageWidth - margin - 2, yPos, { align: 'right' });
      pdf.setTextColor(0, 0, 0); // Reset
      yPos += 6;
    }

    pdf.text('Paid Amount', pageWidth - margin - (totalsBoxWidth - 5), yPos);
    pdf.text(formatCurrencyPdf(inv?.totals?.amountPaid || 0), pageWidth - margin - 2, yPos, { align: 'right' });
    yPos += 6;
    
    // Separator line
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    pdf.line(pageWidth - margin - (totalsBoxWidth - 2), yPos - 2, pageWidth - margin - 2, yPos - 2);
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('Grand Total', pageWidth - margin - (totalsBoxWidth - 5), yPos + 2);
    pdf.text(formatCurrencyPdf(inv?.totals?.grandTotal || 0), pageWidth - margin - 2, yPos + 2, { align: 'right' });
    yPos += 12;

    // Payment
    checkPageBreak(20);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Payment', margin, yPos);
    yPos += 6;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Mode: ${escapeHtml(preview?.payment?.mode || '-')}`, margin, yPos);
    pdf.text(`Due: ${formatCurrencyPdf(preview?.payment?.due || 0)}`, margin + contentWidth / 2, yPos);
    yPos += 5;
    pdf.text(`Collected By: ${escapeHtml(preview?.payment?.collectedBy?.name || '-')}`, margin, yPos);

    const filename = `${preview?.invoice?.invoiceNumber || 'invoice'}.pdf`;
    pdf.save(filename);
  } catch (e) {
    console.error('Failed to generate invoice PDF', e);
    alert('Failed to generate PDF. Please try again.');
  }
};
